import { Badge } from '@ordo/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { Page } from 'compositions/Page'
import { useDashboard, useDeliveryStats } from 'hooks/api'
import { Activity, Calendar, Mail, MessageSquare, MousePointerClick, Send, TriangleAlert, Users } from 'lucide-react'
import type { DeliveryStats, RecentSend, SendStats, UpcomingSchedule } from 'types/dashboard'

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatFutureDate(epochMs: number): string {
  const date = new Date(epochMs)
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  let relative: string
  if (diffDays > 1) {
    relative = `in ${diffDays} days`
  } else if (diffHours > 1) {
    relative = `in ${diffHours} hours`
  } else if (diffMs > 0) {
    relative = 'soon'
  } else {
    relative = 'overdue'
  }

  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} (${relative})`
}

function StatusBadge({ status }: { status: RecentSend['status'] }) {
  const variantMap: Record<RecentSend['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
    SENT: 'default',
    PENDING: 'secondary',
    PARTIAL: 'outline',
    FAILED: 'destructive',
  }
  return <Badge variant={variantMap[status]}>{status}</Badge>
}

function MethodIcon({ method }: { method: 'EMAIL' | 'SMS' }) {
  if (method === 'EMAIL') {
    return <Mail className="h-4 w-4 text-muted-foreground" />
  }
  return <MessageSquare className="h-4 w-4 text-muted-foreground" />
}

function StatCard({
  title,
  value,
  icon: Icon,
  subtitle,
}: {
  title: string
  value: string | number
  icon: React.ComponentType<{ className?: string }>
  subtitle?: string
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4 sm:p-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 sm:h-12 sm:w-12">
          <Icon className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-muted-foreground sm:text-sm">{title}</p>
          <p className="text-xl font-bold sm:text-2xl">{value}</p>
          {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(rate < 0.1 && rate > 0 ? 2 : 1)}%`
}

function DeliveryStatsCard({ stats, loading }: { stats: DeliveryStats | null; loading: boolean }) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Activity className="h-4 w-4" />
            Email Delivery (SendGrid)
          </CardTitle>
        </CardHeader>
        <Separator />
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!stats) {
    return (
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Activity className="h-4 w-4" />
            Email Delivery (SendGrid)
          </CardTitle>
        </CardHeader>
        <Separator />
        <CardContent className="py-8">
          <p className="text-center text-sm text-muted-foreground">
            SendGrid stats unavailable. Verify the API key in admin &rsaquo; integrations.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (stats.requests === 0) {
    return (
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Activity className="h-4 w-4" />
            Email Delivery (last {stats.periodDays} days)
          </CardTitle>
        </CardHeader>
        <Separator />
        <CardContent className="py-8">
          <p className="text-center text-sm text-muted-foreground">
            No SendGrid activity in the last {stats.periodDays} days.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Activity className="h-4 w-4" />
          Email Delivery (last {stats.periodDays} days)
        </CardTitle>
      </CardHeader>
      <Separator />
      <CardContent className="pt-4">
        <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 sm:gap-4 lg:grid-cols-4">
          <StatCard
            title="Sent"
            value={stats.requests.toLocaleString()}
            icon={Send}
            subtitle={`${stats.delivered.toLocaleString()} delivered (${formatPercent(stats.deliveryRate)})`}
          />
          <StatCard
            title="Bounce Rate"
            value={formatPercent(stats.bounceRate)}
            icon={TriangleAlert}
            subtitle={`${(stats.bounces + stats.blocks + stats.invalidEmails).toLocaleString()} bounced/blocked`}
          />
          <StatCard
            title="Open Rate"
            value={formatPercent(stats.openRate)}
            icon={Mail}
            subtitle={`${stats.uniqueOpens.toLocaleString()} unique opens`}
          />
          <StatCard
            title="Click Rate"
            value={formatPercent(stats.clickRate)}
            icon={MousePointerClick}
            subtitle={`${stats.uniqueClicks.toLocaleString()} unique clicks`}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function StatsRow({ stats }: { stats: SendStats }) {
  const successRate = stats.totalSends > 0 ? Math.round(((stats.byStatus['SENT'] ?? 0) / stats.totalSends) * 100) : 0

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      <StatCard title="Total Sends" value={stats.totalSends} icon={Send} />
      <StatCard title="Total Recipients" value={stats.totalRecipients} icon={Users} />
      <StatCard
        title="Success Rate"
        value={`${successRate}%`}
        icon={Activity}
        subtitle={`${stats.byStatus['SENT'] ?? 0} of ${stats.totalSends} succeeded`}
      />
      <StatCard
        title="Failed"
        value={(stats.byStatus['FAILED'] ?? 0) + (stats.byStatus['PARTIAL'] ?? 0)}
        icon={Activity}
        subtitle="Failed or partial sends"
      />
    </div>
  )
}

function sendDisplayName(send: RecentSend): string {
  if (send.scheduledCommunication) {
    return send.scheduledCommunication.name
  }
  if (send.renderedSubject) {
    return send.renderedSubject
  }
  if (send.template) {
    return send.template.name
  }
  return `${send.method} send`
}

function RecentSendsTable({ sends }: { sends: RecentSend[] }) {
  if (sends.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No sends yet</p>
  }

  return (
    // min-w-0 replaces the table's min-w-max so the subject truncates inside
    // the card instead of stretching the page wider than a phone.
    <Table className="min-w-0">
      <TableHeader>
        <TableRow>
          <TableHead>Communication</TableHead>
          <TableHead className="hidden sm:table-cell">Method</TableHead>
          <TableHead>Recipients</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="hidden sm:table-cell">Date</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sends.map((send) => (
          <TableRow key={send.id}>
            <TableCell className="w-full max-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 sm:hidden">
                  <MethodIcon method={send.method} />
                </span>
                <span className="min-w-0 truncate font-medium">{sendDisplayName(send)}</span>
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground sm:hidden">{formatDate(send.startedAt)}</div>
            </TableCell>
            <TableCell className="hidden sm:table-cell">
              <div className="flex items-center gap-1.5">
                <MethodIcon method={send.method} />
                <span className="text-sm">{send.method}</span>
              </div>
            </TableCell>
            <TableCell>
              <span className="font-mono text-sm">{send.recipientCount}</span>
              {send.failureCount > 0 && (
                <span className="ml-1 text-xs text-destructive">({send.failureCount} failed)</span>
              )}
            </TableCell>
            <TableCell>
              <StatusBadge status={send.status} />
            </TableCell>
            <TableCell className="hidden whitespace-nowrap sm:table-cell">{formatDate(send.startedAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function UpcomingSchedulesList({ schedules }: { schedules: UpcomingSchedule[] }) {
  if (schedules.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No upcoming scheduled communications</p>
  }

  return (
    <div className="flex flex-col gap-3">
      {schedules.map((schedule) => (
        <div key={schedule.id} className="flex items-center gap-3 rounded-lg border border-border/50 p-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
            <MethodIcon method={schedule.scheduledCommunication.method} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{schedule.scheduledCommunication.name}</p>
            {schedule.audience.length > 0 && (
              <p className="truncate text-xs text-muted-foreground">{schedule.audience.join(', ')}</p>
            )}
            {schedule.nextExecutionTime && (
              <p className="text-xs text-muted-foreground">{formatFutureDate(schedule.nextExecutionTime)}</p>
            )}
          </div>
          <Badge variant="outline" className="shrink-0">
            {schedule.scheduledCommunication.method}
          </Badge>
        </div>
      ))}
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="flex items-center gap-4 p-4 sm:p-6">
              <Skeleton className="h-10 w-10 rounded-lg sm:h-12 sm:w-12" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 w-12" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <CardHeader>
            <Skeleton className="h-5 w-32" />
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export function Dashboard() {
  const { data, loading } = useDashboard()
  // Fetched separately from the DB-backed dashboard payload: SendGrid's API
  // round trip must never block the rest of the page.
  const deliveryStats = useDeliveryStats()

  return (
    <Page>
      <div className="mb-4 sm:mb-6">
        <h1 className="text-lg font-semibold sm:text-xl">Home</h1>
        <p className="text-sm text-muted-foreground">Communication activity and upcoming sends</p>
      </div>

      {loading || !data ? (
        <DashboardSkeleton />
      ) : (
        <div className="flex flex-col gap-4 sm:gap-6">
          <StatsRow stats={data.stats} />

          <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
            <Card className="min-w-0 lg:col-span-2">
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Send className="h-4 w-4" />
                  Recent Sends
                </CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="p-0 sm:p-0">
                <RecentSendsTable sends={data.recentSends} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Upcoming Communications
                </CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="pt-4">
                <UpcomingSchedulesList schedules={data.upcomingSchedules} />
              </CardContent>
            </Card>
          </div>

          <DeliveryStatsCard stats={deliveryStats.data} loading={deliveryStats.loading} />
        </div>
      )}
    </Page>
  )
}
